import { useDispatch, useSelector } from 'react-redux';

// Design System
import logo from '@assets/images/logos/Project-Z_Animated-Dog_Logo.svg';
import { TickCircleOutlined } from 'jl-design-system/components/icons/TickCircleOutlined';
import Link from 'jl-design-system/elements/link/Link';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import headerState from './Header.state';
import routeConstants, { accountCreationRoutes } from '@constants/routeConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { openLeaveCheckoutModal } from '@redux/actions/app/appActions';
import returnToJL from '@utils/helpers/returnToJL';

// Components
import BodyText from '@components/body-text';
import Container from '@components/container';

// Styles
import styles from './header.scss';

const Header = () => {
  const dispatch: AppDispatch = useDispatch();
  const { isLoading, order, orderComplete, pathname = './', showFullScreenSignUpFlow } = useSelector(headerState);
  const { desktopResolution } = useWindowSize();

  const onLeaveCheckout = () => dispatch(openLeaveCheckoutModal(pathname));

  const HeaderLogoLink = () => {
    const showLeaveCheckoutModal =
      pathname?.includes(routeConstants.SECURE) ||
      pathname?.includes(routeConstants.DELIVERY) ||
      pathname?.includes(routeConstants.CLICK_AND_COLLECT) ||
      pathname?.includes(routeConstants.PAYMENT);
    const Tag = showLeaveCheckoutModal ? 'button' : Link;

    const commonProps = {
      className: styles.headerLogoLink,
      'data-testid': 'header-logo-link',
    };

    const tagSpecificProps = showLeaveCheckoutModal
      ? { onClick: onLeaveCheckout, type: 'button' }
      : { isExternal: true, to: returnToJL() };

    return (
      <Tag {...commonProps} {...tagSpecificProps}>
        <img alt="" aria-hidden="true" className={styles.logo} src={logo} />
        <span className="visually-hidden">John Lewis</span>
      </Tag>
    );
  };

  const isPathForAccountCreation = accountCreationRoutes.some((routeConstant) => pathname?.includes(routeConstant));

  const showAccountCreatedHeader = isPathForAccountCreation && !isLoading && orderComplete && showFullScreenSignUpFlow;

  const secureMessageLabel = desktopResolution ? 'Secure checkout' : 'Secure';
  const secureMessageProps = {
    flex: {
      alignItems: 'center',
      gap: desktopResolution ? '1' : undefined,
      flexDirection: desktopResolution ? undefined : 'col-reverse',
    },
  } as const;

  return (
    <>
      <Container borderBottom="grey-15" className={styles.header} paddingY="3" tag="header" testId="app-header">
        <Container className={styles.container} marginX={desktopResolution ? '4' : '2'}>
          <img alt="John Lewis & Partners" className={styles.printLogo} src={logo} />

          <Container flex={{ alignItems: 'end', justifyContent: 'space-between' }} hideForPrint>
            <HeaderLogoLink />

            <Container className={styles.secureText} testId="header-secure-message" {...secureMessageProps}>
              {secureMessageLabel}
              <span />
            </Container>
          </Container>
          <p className={styles.printOrderId} data-testid="header-order-id">
            Order number:{' '}
            <span>
              <strong>{order?.id}</strong>
            </span>
          </p>
        </Container>
      </Container>
      {showAccountCreatedHeader && (
        <Container borderBottom="grey-15" paddingY="3" textAlign="center">
          <Container flex={{ alignItems: 'center', gap: '0.5', justifyContent: 'center' }}>
            <TickCircleOutlined size="large" />
            <BodyText uppercase>Account created</BodyText>
          </Container>
        </Container>
      )}
    </>
  );
};

export default Header;
